import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { useTheme } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import { Lock } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { NodeActionsContext } from "./context/NodeActionsContext";
import withNodeActions from "./HOC/withNodeActions";
import ButtonEdge from "./Edges/ButtonEdge";
import { useStyles } from "./flowEditorStyles";
import useFlowEditor from "../hooks/useFlowEditor";
import NodeConfigModal from "./Modals/NodeConfigModal";
import FlowLintPanel from "./validation/FlowLintPanel";
import { lintFlow } from "./validation/flowLinter";
import FlowSimulatorPanel from "./simulator/FlowSimulatorPanel";
import useFlowSimulator from "./simulator/useFlowSimulator";
import FlowToolbar from "../components/FlowToolbar";
import NodePalette from "../components/NodePalette";
import { MIME_NODO_PALETA } from "./nodeCategoriesConfig";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import PublishFlowDialog from "./PublishFlowDialog";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal";
import StartNode from "./Nodes/StartNode";
import TextNode from "./Nodes/TextNode";
import ImageNode from "./Nodes/ImageNode";
import VideoNode from "./Nodes/VideoNode";
import AudioNode from "./Nodes/AudioNode";
import DocumentNode from "./Nodes/DocumentNode";
import ConditionNode from "./Nodes/ConditionNode";
import InputNode from "./Nodes/InputNode";
import MenuNode from "./Nodes/MenuNode";
import TagNode from "./Nodes/TagNode";
import RemoveTagNode from "./Nodes/RemoveTagNode";
import OpenAINode from "./Nodes/OpenAINode";
import HTTPRequestNode from "./Nodes/HTTPRequestNode";
import WaitNode from "./Nodes/WaitNode";
import TypingNode from "./Nodes/TypingNode";
import SetVariableNode from "./Nodes/SetVariableNode";
import TransferQueueNode from "./Nodes/TransferQueueNode";
import JumpToFlowNode from "./Nodes/JumpToFlowNode";
import CloseTicketNode from "./Nodes/CloseTicketNode";
import FileInputNode from "./Nodes/FileInputNode";
import ContentNode from "./Nodes/ContentNode";
import ButtonsNode from "./Nodes/ButtonsNode";
import HsmTemplateNode from "./Nodes/HsmTemplateNode";

// ✅ Tipos de nodos — con HOC de acciones rápidas (duplicar / eliminar)
// Definidos a nivel de módulo para que la referencia sea estable entre renders (I-1)
const nodeTypes = {
  start:         withNodeActions(StartNode,         "start"),
  text:          withNodeActions(TextNode,          "text"),
  message:       withNodeActions(TextNode,          "message"),
  image:         withNodeActions(ImageNode,         "image"),
  video:         withNodeActions(VideoNode,         "video"),
  audio:         withNodeActions(AudioNode,         "audio"),
  document:      withNodeActions(DocumentNode,      "document"),
  condition:     withNodeActions(ConditionNode,     "condition"),
  input:         withNodeActions(InputNode,         "input"),
  menu:          withNodeActions(MenuNode,          "menu"),
  tag:           withNodeActions(TagNode,           "tag"),
  removeTag:     withNodeActions(RemoveTagNode,     "removeTag"),
  openai:        withNodeActions(OpenAINode,        "openai"),
  httpRequest:   withNodeActions(HTTPRequestNode,   "httpRequest"),
  wait:          withNodeActions(WaitNode,          "wait"),
  typing:        withNodeActions(TypingNode,        "typing"),
  setVariable:   withNodeActions(SetVariableNode,   "setVariable"),
  transferQueue: withNodeActions(TransferQueueNode, "transferQueue"),
  jumpToFlow: withNodeActions(JumpToFlowNode, "jumpToFlow"),
  closeTicket: withNodeActions(CloseTicketNode, "closeTicket"),
  fileInput: withNodeActions(FileInputNode, "fileInput"),
  content:       withNodeActions(ContentNode,       "content"),
  buttons:       withNodeActions(ButtonsNode,       "buttons"),
  hsmTemplate:   withNodeActions(HsmTemplateNode,   "hsmTemplate"),
};

// ✅ Tipos de aristas — botón de borrado en el centro
/** Medio nodo típico: el punto de soltado queda en el CENTRO del nodo, no en su esquina. */
const MEDIO_NODO = { x: 110, y: 40 };

const edgeTypes = {
  buttonEdge: ButtonEdge,
};

const FlowEditorReactFlow = ({ flowId, initialFlow, onSave, onCancel, onDiscardDraft }) => {
  const classes = useStyles();

  // Borrador y publicación (0422 R4). `borrador` = hay cambios guardados que el motor aún no usa.
  const [borrador, setBorrador] = useState(Boolean(initialFlow?.hayBorrador));
  const [publicarAbierto, setPublicarAbierto] = useState(false);
  const [descartarAbierto, setDescartarAbierto] = useState(false);
  useEffect(() => { setBorrador(Boolean(initialFlow?.hayBorrador)); }, [initialFlow]);
  const onSaveConEstado = useCallback(async (...args) => {
    const r = await onSave(...args);
    if (r?.estado) setBorrador(r.estado === "borrador");
    return r;
  }, [onSave]);
  const theme = useTheme();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [lintPanelOpen, setLintPanelOpen] = useState(false);
  const reactFlowInstanceRef = useRef(null);
  /**
   * Hay una conexion en curso (0419 F3-UX). Solo sirve para encender los estilos que agrandan
   * los destinos: en reposo el lienzo se lee mejor sin doce circulos gordos.
   */
  const [conectando, setConectando] = useState(false);

  /**
   * Qué tipo de conector busca el arrastre en curso: "entrada" o "salida" (0422 QA-UX).
   *
   * Cada lado del nodo tiene una entrada y una salida APILADAS en el mismo punto (NodePorts). Al
   * soltar, React Flow mira el elemento que queda justo bajo el cursor (`elementFromPoint`) y, en
   * modo estricto, rechaza salida→salida. La salida se pinta ENCIMA, así que casi cualquier
   * intento de conectar caía sobre ella y no cuajaba. Con esto, los conectores del tipo que no
   * sirve dejan pasar el ratón y el de debajo es el que se encuentra.
   *
   * Tanto `onConnectStart` como `onEdgeUpdateStart` informan del tipo del extremo FIJO: si es
   * `source`, lo que se busca es una entrada; si es `target` (mover el origen de una flecha),
   * una salida.
   */
  const [buscando, setBuscando] = useState(null);

  const empezarArrastre = useCallback((handleType) => {
    setConectando(true);
    setBuscando(handleType === "target" ? "salida" : "entrada");
  }, []);
  const terminarArrastre = useCallback(() => {
    setConectando(false);
    setBuscando(null);
  }, []);
  // Canal del flujo (0371 WF5): filtra los nodos de la paleta y muestra el badge.
  const flowProvider = initialFlow?.provider || "baileys";

  const {
    nodes, edges, onNodesChange, onEdgesChange,
    flowName, setFlowName,
    saving,
    configModalOpen, setConfigModalOpen,
    selectedNode, setSelectedNode,
    hasUnsavedChanges,
    showCloseConfirmDialog, setShowCloseConfirmDialog,
    handleAddNode,
    handleAutoLayout,
    handleDuplicateNode,
    handleDeleteNode,
    handleDeleteEdge,
    onConnect,
    handleEdgeUpdate,
    handleNodeClick,
    handleSaveNodeConfig,
    handleSave,
    handleCancel,
    handleConfirmClose,
  } = useFlowEditor({ flowId, initialFlow, onSave: onSaveConEstado, onCancel });

  // «Publicar» guarda antes lo que haya sin guardar: se publica lo que se ve.
  const abrirPublicar = async () => {
    if (hasUnsavedChanges) {
      const r = await handleSave();
      if (!r) return;
      if (r.estado !== "borrador") {
        toast.info("No hay cambios que publicar: lo guardado ya es lo que está en producción.");
        return;
      }
    }
    setPublicarAbierto(true);
  };

  /**
   * Colocar nodos nuevos donde se ven (0422 QA-UX). `project()` pasa un punto de la PANTALLA a
   * coordenadas del LIENZO teniendo en cuenta zoom y desplazamiento. Antes el clic ponía el nodo
   * en un punto al azar cerca del origen del lienzo: en un flujo grande o desplazado caía fuera
   * de la vista y había que ir a buscarlo.
   */
  const lienzoRef = useRef(null);
  const clicsSeguidosRef = useRef(0);
  const aLienzo = useCallback((clientX, clientY) => {
    const instancia = reactFlowInstanceRef.current;
    const caja = lienzoRef.current?.getBoundingClientRect();
    if (!instancia || !caja) return undefined;
    const p = instancia.project({ x: clientX - caja.left, y: clientY - caja.top });
    return { x: p.x - MEDIO_NODO.x, y: p.y - MEDIO_NODO.y };
  }, []);

  // Clic en la paleta: al centro de la vista, desplazando un poco cada clic seguido para que
  // varios nodos añadidos de una vez no queden apilados unos sobre otros.
  const anadirEnVista = useCallback(
    (tipo) => {
      const caja = lienzoRef.current?.getBoundingClientRect();
      const n = clicsSeguidosRef.current++ % 6;
      const pos = caja
        ? aLienzo(caja.left + caja.width / 2 + n * 30, caja.top + caja.height / 2 + n * 30)
        : undefined;
      handleAddNode(tipo, pos);
    },
    [aLienzo, handleAddNode]
  );

  const alArrastrarEncima = useCallback((e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes(MIME_NODO_PALETA)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const alSoltar = useCallback(
    (e) => {
      const tipo = e.dataTransfer?.getData(MIME_NODO_PALETA);
      if (!tipo) return;
      e.preventDefault();
      clicsSeguidosRef.current = 0;
      handleAddNode(tipo, aLienzo(e.clientX, e.clientY));
    },
    [aLienzo, handleAddNode]
  );

  // Validación estática (0383 FS-0): se recalcula con cada cambio del grafo.
  const lintResult = useMemo(
    () => lintFlow({ nodes, edges, provider: flowProvider }),
    [nodes, edges, flowProvider]
  );

  const handleFocusNode = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      const instance = reactFlowInstanceRef.current;
      if (!node || !instance) return;
      instance.setCenter(node.position.x + 100, node.position.y + 40, {
        zoom: 1.2,
        duration: 400,
      });
    },
    [nodes]
  );

  // Simulador (0383 FS-2). Se le pasa el grafo del editor tal cual está en pantalla, para
  // poder previsualizar también los cambios sin guardar.
  const getFlowSnapshot = useCallback(
    () => ({
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type,
        label: n.data?.label,
        position: n.position,
        data: n.data,
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || null,
        targetHandle: e.targetHandle || null,
      })),
    }),
    [nodes, edges]
  );

  const simulator = useFlowSimulator({
    flowId,
    provider: flowProvider,
    getFlowSnapshot,
  });

  const { open: simOpen, activeNodeId, visitedNodeIds } = simulator;

  // Auto-centrado en el nodo activo (FS-2.8): el canvas sigue al recorrido.
  useEffect(() => {
    if (simOpen && activeNodeId) handleFocusNode(activeNodeId);
  }, [simOpen, activeNodeId, handleFocusNode]);

  // Resaltado del recorrido (FS-2.7) por className, sin tocar el `data` que se persiste.
  const displayNodes = useMemo(() => {
    if (!simOpen) return nodes;
    return nodes.map((node) => {
      const isActive = node.id === activeNodeId;
      const isVisited = visitedNodeIds.includes(node.id);
      const className = isActive
        ? "sim-active"
        : isVisited
        ? "sim-visited"
        : "sim-dimmed";
      return { ...node, className };
    });
  }, [nodes, simOpen, activeNodeId, visitedNodeIds]);

  /**
   * Autoajustar y encuadrar. El `fitView` va en un `setTimeout(0)` a proposito: `handleAutoLayout`
   * solo encola el cambio de estado, y si se encuadra en el mismo tick React Flow todavia tiene
   * las posiciones viejas y el lienzo queda descuadrado.
   */
  const handleAutoLayoutYEncuadrar = useCallback(
    (direccion) => {
      handleAutoLayout(direccion);
      setTimeout(() => {
        reactFlowInstanceRef.current?.fitView({ padding: 0.15, duration: 400 });
      }, 0);
    },
    [handleAutoLayout]
  );

  return (
    <Box className={classes.editorContainer}>
      <FlowToolbar
        flowName={flowName}
        setFlowName={setFlowName}
        nodes={nodes}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        onSave={handleSave}
        onCancel={handleCancel}
        onOpenDrawer={() => setMobileDrawerOpen(true)}
        provider={flowProvider}
        lintResult={lintResult}
        onToggleLintPanel={() => setLintPanelOpen((prev) => !prev)}
        onPreview={simulator.start}
        previewDisabled={
          simulator.loading || lintResult.hasBlockingErrors || nodes.length === 0
        }
        previewActive={simulator.open}
        onAutoLayout={handleAutoLayoutYEncuadrar}
        isDraft={Boolean(flowId) && borrador}
        onPublish={flowId ? abrirPublicar : null}
        onDiscardDraft={flowId && onDiscardDraft ? () => setDescartarAbierto(true) : null}
      />

      <Box className={classes.content}>
        <NodePalette
          onAddNode={simulator.open ? () => {} : anadirEnVista}
          mobileOpen={mobileDrawerOpen}
          onDrawerClose={() => setMobileDrawerOpen(false)}
          provider={flowProvider}
        />

        {/* ✅ Canvas con React Flow — NodeActionsContext.Provider (I-3) */}
        <Box
          ref={lienzoRef}
          className={`${classes.reactFlowWrapper}${conectando ? " conectando" : ""}${buscando ? ` busca-${buscando}` : ""}`}
          // Durante la previsualización el lienzo está bloqueado: tampoco se sueltan nodos.
          onDragOver={simulator.open ? undefined : alArrastrarEncima}
          onDrop={simulator.open ? undefined : alSoltar}
        >
          <NodeActionsContext.Provider
            value={{
              onDeleteNode: handleDeleteNode,
              onDuplicateNode: handleDuplicateNode,
              onDeleteEdge: handleDeleteEdge,
            }}
          >
            <ReactFlow
              nodes={displayNodes}
              edges={edges}
              onNodesChange={simOpen ? undefined : onNodesChange}
              onEdgesChange={simOpen ? undefined : onEdgesChange}
              onConnect={simOpen ? undefined : onConnect}
              onConnectStart={simOpen ? undefined : (_e, { handleType }) => empezarArrastre(handleType)}
              // `onConnectEnd` dispara tanto si la conexion cuaja como si se suelta en vacio;
              // por eso el apagado va aqui y no dentro de `onConnect`, que solo corre al acertar.
              onConnectEnd={simOpen ? undefined : terminarArrastre}
              // Pasar `onEdgeUpdate` es LO QUE ENCIENDE los anclajes de reconexion: la libreria
              // los dibuja si y solo si este manejador existe (`typeof onEdgeUpdate !== 'undefined'`).
              onEdgeUpdate={simOpen ? undefined : handleEdgeUpdate}
              onEdgeUpdateStart={simOpen ? undefined : (_e, _edge, handleType) => empezarArrastre(handleType)}
              onEdgeUpdateEnd={simOpen ? undefined : terminarArrastre}
              // El anclaje por defecto mide 10 px, el mismo problema que tenian los conectores.
              edgeUpdaterRadius={18}
              onNodeClick={simOpen ? undefined : handleNodeClick}
              // Canvas bloqueado durante la previsualización (FS-2.9): editar mientras se
              // simula desincroniza lo que se ve de lo que se está ejecutando.
              nodesDraggable={!simOpen}
              nodesConnectable={!simOpen}
              elementsSelectable={!simOpen}
              onInit={(instance) => {
                reactFlowInstanceRef.current = instance;
              }}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              deleteKeyCode={["Backspace", "Delete"]}
              defaultEdgeOptions={{
                style: {
                  strokeWidth: 3,
                  stroke: theme.palette.type === "dark" ? "#757575" : "#b1b1b7",
                },
                animated: true,
                type: "buttonEdge",
              }}
              // La linea que se arrastra va en color de acento y mas gruesa que las ya
              // trazadas: si es del mismo gris que el resto, no se distingue de una arista.
              connectionLineStyle={{
                strokeWidth: 4,
                stroke: theme.palette.primary.main,
                strokeDasharray: "8 4",
              }}
              snapToGrid={true}
              snapGrid={[20, 20]}
            >
              <Background
                variant="dots"
                gap={20}
                size={1.5}
                color={theme.palette.type === "dark" ? "#424242" : "#e0e0e0"}
                style={{ backgroundColor: theme.palette.background.default }}
              />
              <Controls
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.shadows[2],
                }}
              />
              <MiniMap
                nodeColor={(node) => {
                  if (node.type === "start") return "#667eea";
                  if (node.type === "message") return "#2196F3";
                  if (node.type === "condition") return "#FF9800";
                  if (node.type === "content") return "#4a148c";
                  return theme.palette.type === "dark" ? "#757575" : "#b1b1b7";
                }}
                maskColor={
                  theme.palette.type === "dark"
                    ? "rgba(0, 0, 0, 0.3)"
                    : "rgba(0, 0, 0, 0.05)"
                }
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                nodeBorderRadius={8}
              />
            </ReactFlow>
          </NodeActionsContext.Provider>

          {simOpen && (
            <Box className={classes.simLockBanner}>
              <Lock style={{ fontSize: 16 }} />
              <Typography variant="caption">
                {i18n.t("flowbuilders.simulator.canvasLocked") ||
                  "El área de trabajo está bloqueada durante la vista previa"}
              </Typography>
            </Box>
          )}

          <FlowLintPanel
            open={lintPanelOpen}
            onClose={() => setLintPanelOpen(false)}
            result={lintResult}
            onFocusNode={handleFocusNode}
          />
        </Box>

        <FlowSimulatorPanel
          open={simulator.open}
          loading={simulator.loading}
          session={simulator.session}
          visibleSteps={simulator.visibleSteps}
          isPlaying={simulator.isPlaying}
          provider={flowProvider}
          contact={simulator.contact}
          setContact={simulator.setContact}
          options={simulator.options}
          setOptions={simulator.setOptions}
          whatsappId={simulator.whatsappId}
          setWhatsappId={simulator.setWhatsappId}
          onReply={simulator.reply}
          onRestart={simulator.restart}
          onClose={simulator.stop}
        />
      </Box>

      <NodeConfigModal
        flowId={flowId}
        open={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedNode(null);
        }}
        node={selectedNode}
        onSave={handleSaveNodeConfig}
      />

      <PublishFlowDialog
        open={publicarAbierto}
        flowId={flowId}
        blockingErrors={lintResult.hasBlockingErrors}
        onClose={() => setPublicarAbierto(false)}
        onPublished={() => setBorrador(false)}
      />

      <ConfirmationModal
        title="Descartar borrador"
        open={descartarAbierto}
        onClose={() => setDescartarAbierto(false)}
        onConfirm={() => onDiscardDraft && onDiscardDraft()}
      >
        Se perderán los cambios guardados sin publicar y el editor volverá a la versión que usan las conversaciones.
      </ConfirmationModal>

      <UnsavedChangesDialog
        open={showCloseConfirmDialog}
        onClose={() => setShowCloseConfirmDialog(false)}
        onConfirm={handleConfirmClose}
      />
    </Box>
  );
};

export default FlowEditorReactFlow;
